import React, { useEffect, useRef, useState } from 'react';
import { useTolgee, useTranslate } from '@tolgee/react';
import Flag from 'react-world-flags';
import dayjs from 'dayjs';
import { ChevronDownIcon } from '../../assets/ChevronDownIcon';
import { cn } from '../../lib/utils';
import { PUBLIC_LANGUAGES, PublicLanguage } from './publicHeader.constants';

interface PublicLanguagePickerProps {
	variant?: 'desktop' | 'mobile';
}

const PublicLanguagePicker: React.FC<PublicLanguagePickerProps> = ({
	variant = 'desktop',
}) => {
	const { t } = useTranslate();
	const tolgee = useTolgee(['language']);
	const [isOpen, setIsOpen] = useState<boolean>(false);
	const rootRef = useRef<HTMLDivElement>(null);
	const closeTimerRef = useRef<number | null>(null);

	const currentCode =
		tolgee.getLanguage() || localStorage.getItem('lang') || 'pl';
	const current =
		PUBLIC_LANGUAGES.find((item) => item.code === currentCode) ??
		PUBLIC_LANGUAGES[0];

	const clearCloseTimer = () => {
		if (closeTimerRef.current !== null) {
			window.clearTimeout(closeTimerRef.current);
			closeTimerRef.current = null;
		}
	};

	const scheduleClose = () => {
		clearCloseTimer();
		closeTimerRef.current = window.setTimeout(() => setIsOpen(false), 200);
	};

	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClick);
		return () => {
			document.removeEventListener('mousedown', handleClick);
			clearCloseTimer();
		};
	}, []);

	const changeLanguage = (language: PublicLanguage) => {
		setIsOpen(false);
		if (language.code === currentCode) return;
		localStorage.setItem('lang', language.code);
		dayjs.locale(language.code);
		tolgee.changeLanguage(language.code).then(() => {});
	};

	const options = [...PUBLIC_LANGUAGES].sort((a, b) =>
		t(a.labelKey, a.defaultLabel).localeCompare(t(b.labelKey, b.defaultLabel)),
	);

	if (variant === 'mobile') {
		return (
			<div className="flex gap-3">
				{options.map((language) => (
					<button
						key={language.code}
						type="button"
						onClick={() => changeLanguage(language)}
						className={cn(
							'flex-1 h-[52px] flex items-center justify-center gap-3 rounded-xl border border-solid font-[inherit] text-base cursor-pointer transition',
							language.code === current.code
								? 'bg-white border-primary-violet text-primary-violet'
								: 'bg-white border-gray-10 text-gray-80',
						)}
					>
						<Flag
							code={language.flag}
							width={21}
							height={16}
							className="object-cover rounded-sm"
						/>
						{t(language.labelKey, language.defaultLabel)}
					</button>
				))}
			</div>
		);
	}

	return (
		<div
			ref={rootRef}
			className="relative flex"
			onMouseEnter={() => {
				clearCloseTimer();
				setIsOpen(true);
			}}
			onMouseLeave={scheduleClose}
		>
			<button
				type="button"
				onClick={() => setIsOpen((value) => !value)}
				className="group flex items-center gap-2 border-none bg-transparent p-0 font-[inherit] text-sm text-gray-80 cursor-pointer"
			>
				<Flag
					code={current.flag}
					width={21}
					height={16}
					className="object-cover rounded-sm"
				/>
				<span>{t(current.labelKey, current.defaultLabel)}</span>
				<span
					className={cn(
						'flex -rotate-90 transition-transform duration-300 group-hover:rotate-0',
						isOpen && 'rotate-0',
					)}
				>
					<ChevronDownIcon color="#747474" width={10} height={6} />
				</span>
			</button>

			{isOpen ? (
				<div
					className="absolute right-0 top-8 min-w-[200px] bg-white rounded-lg shadow-dropdown z-50"
					onMouseEnter={clearCloseTimer}
					onMouseLeave={scheduleClose}
				>
					<ul className="m-0 p-1 list-none">
						{options.map((language) => (
							<li key={language.code}>
								<button
									type="button"
									onClick={() => changeLanguage(language)}
									className="w-full flex items-center gap-3 px-5 py-3 border-none bg-white rounded-md font-[inherit] text-sm text-gray-80 cursor-pointer transition hover:bg-gray-10"
								>
									<Flag
										code={language.flag}
										width={21}
										height={16}
										className="object-cover rounded-sm"
									/>
									{t(language.labelKey, language.defaultLabel)}
								</button>
							</li>
						))}
					</ul>
				</div>
			) : null}
		</div>
	);
};

export default PublicLanguagePicker;
